import { useState, useEffect } from "react"

// ─────────────────────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────────────────────
// The Apps Script URL lives in Vercel as an environment variable (GOOGLE_SCRIPT_URL)
// and is accessed server-side via /api/booked-slots — no URL needed here

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────
const OFFICES = [
  {
    city: "Dallas",
    label: "Dallas",
    date: "Monday, September 28, 2026",
    slots: [
      "10:00 AM", "10:30 AM",
      "1:30 PM", "2:00 PM",
      "3:00 PM", "3:30 PM",
      "4:30 PM",
    ],
  },
]


const TOPICS = [
  "Budgeting & Saving",
  "Debt Paydown Strategy",
  "Retirement Planning",
  "Investing Basics",
  "Building an Emergency Fund",
  "Home Buying Preparation",
  "Employee Benefits & 401(k)",
  "Student Loan Repayment",
  "Credit & Credit Score",
  "Other",
]

const CONFIDENCE_LABELS = {
  1: "Just getting started",
  2: "Still learning the basics",
  3: "Getting the hang of it",
  4: "Pretty confident",
  5: "Very confident",
}

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const COLORS = {
  bg:           "#F4F8FE",
  card:         "#FFFFFF",
  primary:      "#2172EA",
  primaryLight: "#C8DCFA",
  accent:       "#97DFFC",
  accentLight:  "#E5F7FE",
  text:         "#1A1A1A",
  muted:        "#6B7280",
  border:       "#E2DDD7",
  inputBg:      "#FAFAF8",
  successBg:    "#EBF5EE",
  successText:  "#1C4A38",
  pill:         "#F3EFE9",
  pillActive:   "#2172EA",
  pillText:     "#4B4540",
  pillTextActive: "#FFFFFF",
}

const S = {
  wrapper: {
    minHeight: "100vh",
    background: COLORS.bg,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "40px 16px 60px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    background: COLORS.card,
    borderRadius: "20px",
    padding: "0",
    width: "100%",
    maxWidth: "580px",
    overflow: "hidden",
    boxShadow: "0 4px 32px rgba(28,74,56,0.08), 0 1px 4px rgba(0,0,0,0.06)",
  },
  header: {
    background: COLORS.primary,
    padding: "32px 40px 28px",
    color: "#FFFFFF",
  },
  eyebrow: {
    fontSize: "12px",
    fontWeight: "600",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.65)",
    marginBottom: "6px",
    fontFamily: "'Quicksand', sans-serif",
  },
  title: {
    fontSize: "26px",
    fontWeight: "700",
    lineHeight: "1.2",
    margin: "0 0 6px",
    color: "#FFFFFF",
    fontFamily: "'Quicksand', sans-serif",
  },
  subtitle: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.7)",
    margin: 0,
    lineHeight: "1.5",
  },
  stepBar: {
    display: "flex",
    gap: "0",
    padding: "0 40px",
    background: COLORS.primary,
    paddingBottom: "0",
  },
  stepDot: (active, done) => ({
    flex: 1,
    paddingBottom: "12px",
    borderBottom: `3px solid ${active || done ? COLORS.accent : "rgba(255,255,255,0.2)"}`,
    fontSize: "12px",
    fontWeight: "600",
    color: active || done ? COLORS.accent : "rgba(255,255,255,0.45)",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    paddingTop: "12px",
    cursor: "default",
  }),
  body: {
    padding: "36px 40px 40px",
  },
  fieldGroup: {
    marginBottom: "24px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: COLORS.text,
    marginBottom: "8px",
    letterSpacing: "0.01em",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: `1.5px solid ${COLORS.border}`,
    background: COLORS.inputBg,
    fontSize: "15px",
    color: COLORS.text,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
    fontFamily: "inherit",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: COLORS.muted,
    marginBottom: "16px",
    marginTop: "32px",
    paddingTop: "28px",
    borderTop: `1px solid ${COLORS.border}`,
    fontFamily: "'Quicksand', sans-serif",
  },
  confidenceRow: {
    display: "flex",
    gap: "8px",
  },
  confBtn: (active) => ({
    flex: 1,
    padding: "12px 0",
    borderRadius: "10px",
    border: `1.5px solid ${active ? COLORS.primary : COLORS.border}`,
    background: active ? COLORS.primary : COLORS.inputBg,
    color: active ? "#FFFFFF" : COLORS.muted,
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "inherit",
  }),
  confLabel: {
    marginTop: "8px",
    fontSize: "12px",
    color: COLORS.accent,
    fontWeight: "600",
    minHeight: "18px",
    textAlign: "center",
  },
  officeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  officeBtn: (active) => ({
    padding: "14px 12px",
    borderRadius: "12px",
    border: `1.5px solid ${active ? COLORS.primary : COLORS.border}`,
    background: active ? COLORS.primary : COLORS.inputBg,
    color: active ? "#FFFFFF" : COLORS.text,
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s",
    fontFamily: "inherit",
  }),
  officeName: (active) => ({
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "3px",
    color: active ? "#FFFFFF" : COLORS.text,
  }),
  officeDate: (active) => ({
    display: "block",
    fontSize: "11px",
    color: active ? "rgba(255,255,255,0.7)" : COLORS.muted,
  }),
  selectedDateBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: COLORS.accentLight,
    color: COLORS.primary,
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "600",
    marginTop: "14px",
    marginBottom: "4px",
  },
  timeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
  },
  timeBtn: (active, booked) => ({
    padding: "10px 0",
    borderRadius: "8px",
    border: `1.5px solid ${booked ? COLORS.border : active ? COLORS.primary : COLORS.border}`,
    background: booked ? COLORS.pill : active ? COLORS.primary : COLORS.inputBg,
    color: booked ? "#C0BAB3" : active ? "#FFFFFF" : COLORS.pillText,
    fontSize: "13px",
    fontWeight: "600",
    cursor: booked ? "not-allowed" : "pointer",
    textAlign: "center",
    transition: "all 0.15s",
    fontFamily: "inherit",
    textDecoration: booked ? "line-through" : "none",
    opacity: booked ? 0.6 : 1,
  }),
  topicGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  topicBtn: (active) => ({
    padding: "8px 14px",
    borderRadius: "20px",
    border: `1.5px solid ${active ? COLORS.primary : COLORS.border}`,
    background: active ? COLORS.primary : COLORS.inputBg,
    color: active ? "#FFFFFF" : COLORS.pillText,
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "inherit",
  }),
  footer: {
    display: "flex",
    gap: "12px",
    marginTop: "36px",
    paddingTop: "28px",
    borderTop: `1px solid ${COLORS.border}`,
  },
  btnBack: {
    flex: "0 0 auto",
    padding: "14px 22px",
    borderRadius: "12px",
    border: `1.5px solid ${COLORS.border}`,
    background: "transparent",
    color: COLORS.muted,
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnNext: (disabled) => ({
    flex: 1,
    padding: "14px 22px",
    borderRadius: "12px",
    border: "none",
    background: disabled ? "#C8C4BE" : COLORS.primary,
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "700",
    cursor: disabled ? "not-allowed" : "pointer",
    letterSpacing: "0.02em",
    transition: "background 0.15s",
    fontFamily: "inherit",
  }),
  errorMsg: {
    marginTop: "12px",
    padding: "10px 14px",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#B91C1C",
  },
  success: {
    textAlign: "center",
    padding: "64px 40px",
  },
  successIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: COLORS.successBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    fontSize: "28px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: COLORS.text,
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif",
  },
  successBody: {
    fontSize: "15px",
    color: COLORS.muted,
    lineHeight: "1.6",
    maxWidth: "360px",
    margin: "0 auto 24px",
  },
  summaryCard: {
    background: COLORS.successBg,
    borderRadius: "12px",
    padding: "20px 24px",
    textAlign: "left",
    maxWidth: "360px",
    margin: "0 auto",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginBottom: "8px",
    gap: "12px",
  },
  summaryLabel: {
    color: COLORS.muted,
    fontWeight: "500",
    flexShrink: 0,
  },
  summaryValue: {
    color: COLORS.primary,
    fontWeight: "600",
    textAlign: "right",
  },
}

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
export default function MentorSignupForm() {
  const [step, setStep]         = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [error, setError]           = useState("")
  const [focusedInput, setFocusedInput] = useState(null)
  // bookedSlots: Set of "City|||TimeSlot" keys
  const [bookedSlots, setBookedSlots] = useState(new Set())
  const [slotsLoading, setSlotsLoading] = useState(true)

  const [form, setForm] = useState({
    name:                "",
    title:               "",
    email:               "",
    financialConfidence: 0,
    office:              "",
    timeSlot:            "",
    topic:               "",
    notes:               "",
  })

  // Fetch booked slots via same-origin Vercel proxy (avoids CORB/CORS issues with Apps Script)
  useEffect(() => {
    fetch("/api/booked-slots")
      .then((r) => r.json())
      .then((data) => {
        if (data.booked) setBookedSlots(new Set(data.booked))
      })
      .catch((err) => console.error("[Fruition] Could not load booked slots:", err))
      .finally(() => setSlotsLoading(false))
  }, [])

  const isBooked = (city, slot) => bookedSlots.has(`${city}|||${slot}`)

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }))

  const selectedOffice = OFFICES.find((o) => o.city === form.office)

  const step1Valid =
    form.name.trim() &&
    form.title.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.financialConfidence > 0

  const step2Valid =
    form.office && form.timeSlot && form.topic

  const inputStyle = (field) => ({
    ...S.input,
    borderColor: focusedInput === field ? COLORS.primary : COLORS.border,
    boxShadow: focusedInput === field ? `0 0 0 3px rgba(28,74,56,0.08)` : "none",
  })

  const handleSubmit = async () => {
    setSubmitting(true)
    setError("")
    try {
      const res = await fetch("/api/submit", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          name:                form.name,
          title:               form.title,
          email:               form.email,
          financialConfidence: form.financialConfidence,
          office:              form.office,
          date:                selectedOffice?.date ?? "",
          timeSlot:            form.timeSlot,
          topic:               form.topic,
          notes:               form.notes,
          submittedAt:         new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }),
        }),
      })
      // Optimistically mark the slot as booked locally
      setBookedSlots((prev) => new Set([...prev, `${form.office}|||${form.timeSlot}`]))
      setSubmitted(true)
    } catch {
      setError("Something went wrong. Please try again or contact us directly.")
    }
    setSubmitting(false)
  }

  // ── Success screen ──────────────────────────────────────────
  if (submitted) {
    return (
      <div style={S.wrapper}>
        <link
          href="https://fonts.googleapis.com/css2?family=Quicksand:wght@600;700&family=Inter:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
        <div style={S.card}>
          <div style={{ ...S.header, background: COLORS.primary }}>
            <p style={S.eyebrow}>Fruition Wellness Fair</p>
            <h1 style={S.title}>1:1 Mentor Session</h1>
          </div>
          <div style={S.success}>
            <div style={S.successIcon}>✓</div>
            <h2 style={S.successTitle}>You're booked!</h2>
            <p style={S.successBody}>
              We'll send a confirmation to{" "}
              <strong style={{ color: COLORS.primary }}>{form.email}</strong>. See
              you at the {form.office} Wellness Fair!
            </p>
            <div style={S.summaryCard}>
              {[
                ["Location", form.office],
                ["Date",     selectedOffice?.date ?? ""],
                ["Time",     form.timeSlot],
                ["Topic",    form.topic],
              ].map(([label, value]) => (
                <div key={label} style={S.summaryRow}>
                  <span style={S.summaryLabel}>{label}</span>
                  <span style={S.summaryValue}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Form ────────────────────────────────────────────────────
  return (
    <div style={S.wrapper}>
      <link
        href="https://fonts.googleapis.com/css2?family=Quicksand:wght@600;700&family=Inter:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      <div style={S.card}>

        {/* ── Header ── */}
        <div style={S.header}>
          <p style={S.eyebrow}>HKS Wellness Fair · 2026</p>
          <h1 style={S.title}>Book a 1:1 Mentor Session</h1>
          <p style={S.subtitle}>
            Schedule 20 minutes with a financial mentor and get credit towards your wellness incentive!
          </p>
        </div>

        {/* ── Step indicator ── */}
        <div style={S.stepBar}>
          {["Your Info", "Your Session"].map((label, i) => {
            const num    = i + 1
            const active = step === num
            const done   = step > num
            return (
              <div key={label} style={S.stepDot(active, done)}>
                {done ? "✓ " : `${num}. `}{label}
              </div>
            )
          })}
        </div>

        <div style={S.body}>

          {/* ══════════════════════════════════════════════════
              STEP 1 — Personal Info + Confidence
          ══════════════════════════════════════════════════ */}
          {step === 1 && (
            <>
              {/* Name & Title */}
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Full Name</label>
                  <input
                    style={inputStyle("name")}
                    type="text"
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={(e) => set("name")(e.target.value)}
                    onFocus={() => setFocusedInput("name")}
                    onBlur={() => setFocusedInput(null)}
                  />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Job Title</label>
                  <input
                    style={inputStyle("title")}
                    type="text"
                    placeholder="Senior Analyst"
                    value={form.title}
                    onChange={(e) => set("title")(e.target.value)}
                    onFocus={() => setFocusedInput("title")}
                    onBlur={() => setFocusedInput(null)}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={S.fieldGroup}>
                <label style={S.label}>Work Email</label>
                <input
                  style={inputStyle("email")}
                  type="email"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={(e) => set("email")(e.target.value)}
                  onFocus={() => setFocusedInput("email")}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              {/* Financial Confidence */}
              <div style={{ marginTop: "28px" }}>
                <p style={{ ...S.sectionLabel, marginTop: 0, paddingTop: 0, borderTop: "none" }}>
                  Financial Confidence
                </p>
                <label style={{ ...S.label, marginBottom: "12px", fontSize: "14px", fontWeight: "400", color: COLORS.muted }}>
                  How confident do you feel about your finances right now?
                </label>
                <div style={S.confidenceRow}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      style={S.confBtn(form.financialConfidence === n)}
                      onClick={() => set("financialConfidence")(n)}
                      type="button"
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p style={S.confLabel}>
                  {form.financialConfidence
                    ? `${form.financialConfidence} — ${CONFIDENCE_LABELS[form.financialConfidence]}`
                    : "Select a number above"}
                </p>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════
              STEP 2 — Office, Time, Topic
          ══════════════════════════════════════════════════ */}
          {step === 2 && (
            <>
              {/* Office */}
              <div style={{ marginBottom: "28px" }}>
                <label style={S.label}>Which office are you in?</label>
                <div style={S.officeGrid}>
                  {OFFICES.map((o) => {
                    const active = form.office === o.city
                    return (
                      <button
                        key={o.city}
                        style={S.officeBtn(active)}
                        onClick={() => {
                          set("office")(o.city)
                          set("timeSlot")("")
                        }}
                        type="button"
                      >
                        <span style={S.officeName(active)}>{o.city}</span>
                        <span style={S.officeDate(active)}>{o.date}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Time Slot — only shows after office chosen */}
              {selectedOffice && (
                <>
                  <div style={S.selectedDateBadge}>
                    <span style={{ fontSize: "14px" }}>📅</span>
                    {selectedOffice.date}
                  </div>
                  <div style={{ marginTop: "20px", marginBottom: "28px" }}>
                    <label style={S.label}>
                      Pick your 20-minute slot
                      {slotsLoading && (
                        <span style={{ fontWeight: 400, color: COLORS.muted, marginLeft: "8px", fontSize: "12px" }}>
                          checking availability…
                        </span>
                      )}
                    </label>
                    <div style={S.timeGrid}>
                      {selectedOffice.slots.map((t) => {
                        const booked = isBooked(form.office, t)
                        const active = form.timeSlot === t
                        return (
                          <button
                            key={t}
                            style={S.timeBtn(active, booked)}
                            onClick={() => !booked && set("timeSlot")(t)}
                            type="button"
                            disabled={booked}
                            title={booked ? "This slot is already taken" : undefined}
                          >
                            {t}
                            {booked && (
                              <span style={{ display: "block", fontSize: "10px", fontWeight: 500, marginTop: "2px" }}>
                                taken
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Topic */}
              <div>
                <label style={S.label}>What would you like to discuss?</label>
                <div style={S.topicGrid}>
                  {TOPICS.map((t) => (
                    <button
                      key={t}
                      style={S.topicBtn(form.topic === t)}
                      onClick={() => set("topic")(t)}
                      type="button"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginTop: "24px" }}>
                <label style={S.label}>
                  Anything else you'd like to share?{" "}
                  <span style={{ fontWeight: 400, color: COLORS.muted }}>(optional)</span>
                </label>
                <textarea
                  style={{
                    ...S.input,
                    height: "100px",
                    resize: "vertical",
                    lineHeight: "1.6",
                    borderColor: focusedInput === "notes" ? COLORS.primary : COLORS.border,
                    boxShadow: focusedInput === "notes" ? `0 0 0 3px rgba(28,74,56,0.08)` : "none",
                  }}
                  placeholder="Share any context that would help your mentor prepare — specific questions, recent life changes, goals you're working toward…"
                  value={form.notes}
                  onChange={(e) => set("notes")(e.target.value)}
                  onFocus={() => setFocusedInput("notes")}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>
            </>
          )}

          {/* ── Navigation ── */}
          <div style={S.footer}>
            {step === 2 && (
              <button
                style={S.btnBack}
                onClick={() => setStep(1)}
                type="button"
              >
                ← Back
              </button>
            )}
            {step === 1 && (
              <button
                style={S.btnNext(!step1Valid)}
                disabled={!step1Valid}
                onClick={() => step1Valid && setStep(2)}
                type="button"
              >
                Next: Choose Your Session →
              </button>
            )}
            {step === 2 && (
              <button
                style={S.btnNext(!step2Valid || submitting)}
                disabled={!step2Valid || submitting}
                onClick={handleSubmit}
                type="button"
              >
                {submitting ? "Submitting…" : "Reserve My Spot →"}
              </button>
            )}
          </div>

          {error && <p style={S.errorMsg}>{error}</p>}
        </div>
      </div>
    </div>
  )
}
